import { assertInputPolicy, } from "./input-policy.js";
export const REMOTE_RISK_CLASSES = ["read", "write"];
/**
 * The control vocabulary a generated edit form may use.
 *
 * A CLOSED set, and closed is the point: the browser renders these with design
 * system components, so a value outside the set has no renderer. It is platform
 * vocabulary rather than provider vocabulary — "a short string" is not an
 * Atlassian concept — which is why it can live here.
 */
export const REMOTE_FORM_CONTROLS = [
    "text",
    "textarea",
    "number",
    "toggle",
    "select",
    "items",
];
export function checkFormHintsAgainstSchema(hints, schema) {
    if (hints === undefined || hints.size === 0) {
        return { unmatched: [], securitySensitive: [] };
    }
    const properties = schema !== null && typeof schema === "object" && !Array.isArray(schema)
        ? schema["properties"]
        : undefined;
    // A schema with NO usable properties object matches nothing, so every hint is
    // unmatched. Deliberately not treated as "nothing to check": that is the
    // shape a truncated or malformed schema has, and it is exactly when an
    // immutable destination would silently become editable.
    const declared = properties !== null &&
        typeof properties === "object" &&
        !Array.isArray(properties)
        ? new Set(Object.keys(properties))
        : new Set();
    const unmatched = [];
    const securitySensitive = [];
    for (const [name, hint] of hints) {
        if (declared.has(name))
            continue;
        unmatched.push(name);
        if (hint.readOnly === true)
            securitySensitive.push(name);
    }
    return { unmatched, securitySensitive };
}
/** The shape a classification token must have. Lowercase, bounded, no free text. */
const SERVICE_KIND_TOKEN = /^[a-z][a-z0-9_]{0,31}$/;
/**
 * Is this a well-formed classification token?
 *
 * Shape, not membership — see `RemoteServiceKind`. It exists so a malformed
 * token is caught at boot rather than travelling to a presentation layer whose
 * only safe response is to discard it silently.
 */
export function isServiceKindToken(value) {
    return typeof value === "string" && SERVICE_KIND_TOKEN.test(value);
}
/**
 * Why a discovered tool cannot be represented, when it cannot.
 *
 * A defect is not a failure to discover the tool — the tool is reported either
 * way, because an administrator reviewing a catalogue needs to see that it
 * exists. It is the reason the row can never be ENABLED: without a schema there
 * is nothing to validate arguments against, nothing to hash, and therefore no
 * way to bind an approval to a specific request.
 *
 * Lives in the kernel rather than in whichever adapter discovered it, because
 * the persistence layer and the governance UI both speak it, and two copies of
 * a vocabulary that crosses that many layers is two things to keep in step.
 *
 * The four are four different LAYERS noticing, which is why they stay
 * distinct rather than collapsing into one "bad schema":
 *
 *  - `schema_missing` and `schema_too_large` are TRANSPORT facts. The server
 *    sent no object, or one too big to store and hash.
 *  - `schema_unsupported` is a GOVERNANCE fact. The schema arrived intact and
 *    describes constructs this platform will not compile a validator from, so
 *    the tool's arguments could never be checked against what an administrator
 *    approved. Surfaced at DISCOVERY rather than at execution: an administrator
 *    must not be able to enable a tool and only find out it is unusable when a
 *    member tries to run it.
 *  - `presentation_policy_mismatch` is a CONNECTOR fact. The schema is fine and
 *    the platform can validate it; what disagrees is the connector's own
 *    presentation policy — a hint asserting that a field is an immutable
 *    destination names a field this schema does not have. The hint is then
 *    inert, and the destination it was protecting renders EDITABLE, so a human
 *    could retarget a write they were shown as landing somewhere specific.
 *    Quarantining is the only safe reading: silently ignoring the hint ships
 *    the editable destination, and silently honouring it would mean guessing
 *    which field was meant. See `checkFormHintsAgainstSchema`.
 */
export const REMOTE_TOOL_DEFECTS = [
    "schema_missing",
    "schema_too_large",
    "schema_unsupported",
    "presentation_policy_mismatch",
];
/**
 * Where a discovered tool sits in the review cycle.
 *
 * `quarantined` is the state a NEW tool and a CHANGED schema both land in. It is
 * distinct from `disabled` because they mean different things to an
 * administrator: disabled is a decision, quarantined is a decision not yet made.
 * `withdrawn` is the server having stopped offering it — kept rather than
 * deleted, because executions and audit rows still reference it.
 */
export const REMOTE_TOOL_LIFECYCLE = [
    "quarantined",
    "enabled",
    "disabled",
    "withdrawn",
];
/**
 * The ONE predicate that decides whether a governed tool may be invoked.
 *
 * Written once, here, so no call site can answer it differently — the same
 * reason `isRetryable` lives in the kernel rather than in the retry loop. Every
 * clause is a way the answer must be no: not reviewed, not classified, no
 * schema to validate arguments against, or a schema this platform will not
 * build a validator from.
 *
 * Approval survives schema updates, including a temporarily unusable schema.
 * This predicate keeps technical availability separate from that decision:
 * an enabled but defective tool is never callable.
 */
export function isRemoteToolCallable(tool) {
    return (tool.lifecycle === "enabled" &&
        tool.riskClass !== null &&
        tool.inputSchema !== null &&
        tool.defect === null);
}
export function asRemoteRead(tool) {
    if (!isRemoteToolCallable(tool))
        return null;
    if (tool.riskClass !== "read")
        return null;
    if (tool.inputSchema === null)
        return null;
    return tool;
}
/**
 * A connector-owned safety guard that STRADDLES one write.
 *
 * ## Why `before` exists when a preflight already ran
 *
 * `RemoteToolPreflight` runs at PREPARE, which is where a refusal belongs: it
 * reaches a human while they are still deciding, before an approval is sought.
 * It is not sufficient. An approval can wait — minutes, or until somebody comes
 * back from lunch — and the provider object can change underneath it. The
 * authoritative check is therefore re-run by the WORKER, immediately before the
 * write, and that is what `before` is.
 *
 * ## Why `before` hands its evidence forward
 *
 * "Did this write destroy something?" cannot be answered from the write's own
 * response. It needs the object as it stood BEFORE — and re-reading afterwards
 * is too late. So `before` returns bounded EVIDENCE, the engine keeps it in
 * memory for the duration of the write, and hands it back to `after`.
 *
 * **The evidence is ephemeral by contract.** It is never persisted, never
 * audited and never logged: it is derived from a provider object and can carry
 * tenant content. A connector must keep it small for the same reason.
 *
 * ## Why `after` is async and gets a transport
 *
 * A response can echo a different object's metadata than the one written, so
 * confirming a write means RE-READING it — which needs I/O, and a read-only
 * transport is what makes "a verification step cannot itself write" structural
 * rather than a promise.
 *
 * ## What `inconsistent` means to the engine
 *
 * Not failure — the write may well have landed, possibly on the wrong object.
 * It routes to RECONCILIATION, never to a retry, which is the only honest
 * handling of "something happened and we cannot say what". Missing, mismatched
 * or unreadable evidence after a write is `inconsistent`, not a pass.
 *
 * ## Provider-neutral by construction
 *
 * The kernel learns no provider's field names. "Compare what I asked for
 * against what came back, then read it again" is a shape every provider has,
 * and only the connector knows where those values live.
 */
/**
 * The ceiling on a guard's ephemeral evidence, in UTF-8 bytes of its JSON form.
 *
 * ONE named bound, enforced by the ENGINE rather than trusted from a connector,
 * because "bounded" was previously a comment. A connector that returns a page
 * body, a cyclic object or nothing at all is refused BEFORE the provider write
 * — the cheapest moment, and the only one where refusing costs nothing.
 *
 * Sized for DERIVED facts: counts, ordered digests, a handful of identifiers.
 * 64 KiB is far above anything a digest list needs and far below a page body,
 * so a breach means a connector is carrying content it should have hashed.
 */
export const MAX_WRITE_EVIDENCE_BYTES = 64 * 1024;
/**
 * Whether a guard's evidence satisfies the contract above — and a frozen copy
 * of it when it does.
 *
 * Lives in the kernel so the engine and the conformance kit ask the identical
 * question — two implementations of "is this bounded" would eventually disagree
 * about the case that matters.
 *
 * ## Three properties, each of which was once absent
 *
 * **1. It NEVER throws, for any JavaScript value.** A guard's return value is
 * connector-supplied and reaches this function before the provider call, in the
 * engine's pre-write path where a throw escapes as an unhandled exception
 * rather than a contract refusal. A `Proxy` whose `getPrototypeOf` trap throws
 * did exactly that. Proxies are now rejected structurally AND the whole walk
 * sits inside a catch, because a trap can throw from `ownKeys`,
 * `getOwnPropertyDescriptor`, `has` or `get` and enumerating the ways is not a
 * safety argument.
 *
 * **2. It rejects everything `JSON.stringify` would DROP, ADD, INVOKE or
 * REWRITE**, not merely everything that makes stringify fail. Four shapes used
 * to pass and each is a silent rewrite:
 *
 *   - a SPARSE array — `[1, , , 2]` has own keys `0`, `3`, `length`, and
 *     serialises to `[1,null,null,2]`. Two `null`s ADDED.
 *   - an array with a NON-INDEX property — `Object.assign([1,2],{note:"x"})`
 *     serialises to `[1,2]`. The property DROPPED.
 *   - a NON-ENUMERABLE own data property — dropped the same way.
 *   - a `Proxy` at any depth — see above, and a non-throwing one can simply
 *     answer differently between the walk and the serialisation.
 *
 * **3. It returns a SNAPSHOT.** Validating the connector's object and then
 * keeping the object is a time-of-check/time-of-use gap with a provider round
 * trip inside it: a guard that retains its returned object can mutate it while
 * the write is in flight, and `after` would then compare against evidence that
 * is not what was validated. The snapshot is built from the descriptor values
 * read during the walk, so it shares no structure with the input.
 */
export function checkWriteEvidence(evidence) {
    try {
        return inspectWriteEvidence(evidence);
    }
    catch {
        // The BACKSTOP, not the mechanism. Everything reachable is rejected
        // structurally above; this exists because "no exotic value can throw here"
        // is a claim about every future JavaScript object, which nobody can make.
        return {
            ok: false,
            reason: "the guard's evidence could not be inspected",
        };
    }
}
function inspectWriteEvidence(evidence) {
    if (evidence === undefined || evidence === null) {
        return {
            ok: false,
            reason: "the guard permitted the write without evidence",
        };
    }
    // STRUCTURE FIRST, bytes second, and the bytes are measured on the SNAPSHOT.
    //
    // `JSON.stringify` is not a validator, so measuring its output on the input
    // says nothing about what the guard produced. The walk below builds a plain
    // deep copy while it validates; serialising THAT is honest, because it holds
    // no getters, no proxies and no exotic prototypes by construction.
    const walked = snapshotJsonData(evidence, new Set(), 0);
    if (!walked.ok)
        return walked;
    const serialised = JSON.stringify(walked.snapshot);
    if (typeof serialised !== "string") {
        return { ok: false, reason: "the guard's evidence is not serialisable" };
    }
    if (Buffer.byteLength(serialised, "utf8") > MAX_WRITE_EVIDENCE_BYTES) {
        return { ok: false, reason: "the guard's evidence exceeds its bound" };
    }
    return walked;
}
/** Depth is bounded too: a deep tree is a cheap way to spend the walk. */
const MAX_EVIDENCE_DEPTH = 20;
const refuse = (reason) => ({ ok: false, reason });
/**
 * Is this value a `Proxy`?
 *
 * There is no language-level answer — a proxy's entire purpose is to be
 * indistinguishable — so this asks the RUNTIME, which reads the internal slot
 * and therefore cannot be spoofed by a trap. It answers `true` for a REVOKED
 * proxy too, which matters: `typeof` still says `object` for one while every
 * reflective operation on it throws.
 *
 * ## Why a global lookup rather than an import
 *
 * `process.getBuiltinModule` is a plain property access on a global, so it is
 * invisible to a bundler. A static `import … from "node:util"` would not be:
 * `@playbookai/api-clients` imports this package and the shell bundles that for
 * the browser, and this file currently reaches Node only through the `Buffer`
 * global for exactly the same reason. The repo pins Node >= 24, where the
 * accessor has been available since 20.16.
 *
 * ## What happens where it is absent
 *
 * Detection degrades, the CONTRACT does not. A throwing or revoked proxy still
 * refuses, because every reflective step runs inside `checkWriteEvidence`'s
 * catch. A silent, self-consistent proxy would then be walked — and the engine
 * still never sees it, because what leaves this module is the SNAPSHOT, a plain
 * copy of validated data. Stated plainly rather than claimed away: on such a
 * runtime the "no proxies" rule becomes "no proxy can be observed by anything
 * downstream", which is the property the rule exists to buy.
 */
const runtimeIsProxy = (() => {
    try {
        const getBuiltinModule = globalThis.process?.getBuiltinModule;
        if (typeof getBuiltinModule !== "function")
            return null;
        const util = getBuiltinModule("node:util");
        const isProxyFn = util?.types?.isProxy;
        return typeof isProxyFn === "function"
            ? (value) => isProxyFn(value)
            : null;
    }
    catch {
        return null;
    }
})();
function isProxy(value) {
    return runtimeIsProxy !== null && runtimeIsProxy(value);
}
/**
 * Validate one value as plain JSON DATA and copy it in the same pass.
 *
 * "JSON data" is narrower than "JSON.stringify survives it": no `undefined`, no
 * functions, no symbols, no bigints, no `NaN`/`Infinity`, no
 * `Date`/`Map`/`Set`/class instance, no custom `toJSON`, no accessor (getter OR
 * setter), no non-enumerable own property, no proxy, no sparse array, no
 * non-index property on an array, and no cycle. Each is a value that
 * disappears, appears, changes shape, or runs code on the way through — and
 * evidence that changed on the way through is evidence that no longer describes
 * what the guard saw.
 *
 * Validating and copying together is deliberate: two passes over a
 * connector-owned structure could disagree, which is the same
 * time-of-check/time-of-use hole the snapshot exists to close.
 */
function snapshotJsonData(value, seen, depth) {
    if (depth > MAX_EVIDENCE_DEPTH) {
        return refuse("the guard's evidence is nested too deeply");
    }
    if (value === null)
        return { ok: true, snapshot: null };
    const type = typeof value;
    if (type === "string" || type === "boolean") {
        return { ok: true, snapshot: value };
    }
    if (type === "number") {
        return Number.isFinite(value)
            ? { ok: true, snapshot: value }
            : refuse("the guard's evidence contains a non-finite number");
    }
    if (type !== "object") {
        // undefined, function, symbol, bigint — each is dropped or throws.
        return refuse(`the guard's evidence contains a ${type} value`);
    }
    const object = value;
    // BEFORE any reflection on it. Every operation below — `getPrototypeOf`,
    // `Array.isArray`, `ownKeys`, `getOwnPropertyDescriptor`, `in` — is a trap a
    // proxy can intercept, throw from, or answer inconsistently between calls.
    // There is no way to inspect a proxy safely, so it is not inspected.
    if (isProxy(object)) {
        return refuse("the guard's evidence contains a proxy");
    }
    if (seen.has(object))
        return refuse("the guard's evidence is cyclic");
    seen.add(object);
    // A PLAIN object or array, nothing else. `Date`, `Map`, a class instance and
    // anything carrying `toJSON` all serialise into something other than
    // themselves.
    const prototype = Object.getPrototypeOf(object);
    const isArray = Array.isArray(object);
    if (isArray) {
        if (prototype !== Array.prototype) {
            return refuse("the guard's evidence contains a subclassed array");
        }
    }
    else if (prototype !== Object.prototype && prototype !== null) {
        return refuse("the guard's evidence contains a non-plain object");
    }
    if ("toJSON" in object) {
        return refuse("the guard's evidence defines a custom serialiser");
    }
    const result = isArray
        ? snapshotArray(object, seen, depth)
        : snapshotObject(object, seen, depth);
    // Removed on the way out, so a DAG — the same object referenced twice, which
    // serialises perfectly well — is not mistaken for a cycle.
    seen.delete(object);
    return result;
}
/**
 * An array must be DENSE, index-keyed, and carry nothing else.
 *
 * `Reflect.ownKeys` on `[1, , , 2]` answers `["0","3","length"]`: the holes have
 * no own key at all, so a walk that only visits own keys sees a two-element
 * array and `JSON.stringify` emits four. Counting the indices against `length`
 * is what catches it, and the same pass catches `note` on
 * `Object.assign([1,2],{note:"x"})` — an own, enumerable property that
 * stringify drops on the floor.
 */
function snapshotArray(array, seen, depth) {
    const { length } = array;
    let indices = 0;
    for (const key of Reflect.ownKeys(array)) {
        if (typeof key === "symbol") {
            return refuse("the guard's evidence contains a symbol key");
        }
        if (key === "length")
            continue;
        // `String(Number(key)) === key` rejects "01", "1.0", " 1" and "-0" — every
        // spelling that is a property name rather than an index.
        const index = Number(key);
        if (!Number.isInteger(index) || index < 0 || String(index) !== key) {
            return refuse("the guard's evidence puts a named property on an array");
        }
        const descriptor = Object.getOwnPropertyDescriptor(array, key);
        if (descriptor === undefined || !("value" in descriptor)) {
            return refuse("the guard's evidence defines an accessor property");
        }
        if (!descriptor.enumerable) {
            return refuse("the guard's evidence hides a non-enumerable property");
        }
        indices += 1;
    }
    if (indices !== length) {
        return refuse("the guard's evidence contains a sparse array");
    }
    const snapshot = [];
    for (let index = 0; index < length; index += 1) {
        const element = snapshotJsonData(array[index], seen, depth + 1);
        if (!element.ok)
            return element;
        snapshot.push(element.snapshot);
    }
    return { ok: true, snapshot };
}
function snapshotObject(object, seen, depth) {
    const snapshot = {};
    for (const key of Reflect.ownKeys(object)) {
        if (typeof key === "symbol") {
            return refuse("the guard's evidence contains a symbol key");
        }
        const descriptor = Object.getOwnPropertyDescriptor(object, key);
        if (descriptor === undefined) {
            return refuse("the guard's evidence could not be inspected");
        }
        // An ACCESSOR runs code during serialisation, so what is measured is not
        // what was inspected — and a SETTER-only property is an accessor too, with
        // `value` absent rather than `undefined`. `"value" in descriptor` is the
        // test that tells the two apart; reading `descriptor.value` cannot.
        if (!("value" in descriptor)) {
            return refuse("the guard's evidence defines an accessor property");
        }
        // A NON-ENUMERABLE own data property is invisible to `JSON.stringify` and
        // visible to `Reflect.ownKeys`, so it is exactly the shape that passes a
        // walk and vanishes from the bytes.
        if (!descriptor.enumerable) {
            return refuse("the guard's evidence hides a non-enumerable property");
        }
        const child = snapshotJsonData(descriptor.value, seen, depth + 1);
        if (!child.ok)
            return child;
        snapshot[key] = child.snapshot;
    }
    return { ok: true, snapshot };
}
/**
 * Run the connector's binding, or answer `not_applicable` when it declares none.
 *
 * A funnel, for the same reason the lookups below are functions: every call
 * site asks the question identically, and the "no binding means nothing to
 * check" reading lives in one place rather than being re-derived at four.
 */
export function checkTenantBinding(catalogue, input) {
    const binding = catalogue.tenantBinding;
    if (binding === undefined)
        return { kind: "not_applicable" };
    return binding.check(input);
}
/** Project a stored provider schema for a delegated caller, when supported. */
export function projectDelegatedTenantSchema(catalogue, schema) {
    return catalogue.tenantBinding?.delegation?.projectSchema(schema) ?? schema;
}
/** Bind trusted connection identity into delegated arguments, when supported. */
export function bindDelegatedTenantArguments(catalogue, input) {
    return (catalogue.tenantBinding?.delegation?.bindArguments(input) ?? input.arguments);
}
export function hasRemoteCatalogue(definition) {
    return definition.remoteTools !== undefined;
}
/**
 * Lookups. Exact key, `undefined` for anything undeclared.
 *
 * Functions rather than direct map access so every call site asks the question
 * the same way, and so the "exact, never a pattern" rule has one place to live
 * if it ever needs an exception argued for it.
 */
export function preflightFor(catalogue, toolName) {
    return catalogue.preflights?.get(toolName);
}
export function resultAdapterFor(catalogue, toolName) {
    return catalogue.resultAdapters?.get(toolName);
}
export function writeGuardFor(catalogue, toolName) {
    return catalogue.writeGuards?.get(toolName);
}
/**
 * The connector's SUGGESTED class for a tool, for an administrator to read.
 *
 * Deliberately named `suggestion`. Nothing on an execution path calls this —
 * `decideEffectiveToolAccess` reads the stored class — so a connector cannot
 * classify its way past a review by declaring one.
 */
export function riskSuggestionFor(catalogue, toolName) {
    return catalogue.riskSuggestions?.get(toolName);
}
/**
 * The trusted service classification for one tool, or `null`.
 *
 * `null` rather than `undefined` on purpose: "unclassified" is a real, expected
 * answer that callers must render (generically), not an absence they might
 * forget to handle. Every path that shows a service name goes through here, so
 * there is exactly one place where "we do not know" is decided.
 */
export function serviceKindFor(catalogue, toolName) {
    return catalogue.serviceKinds?.get(toolName) ?? null;
}
/** A connector's presentation advice for one tool, or undefined when it wrote none. */
export function formHintsFor(catalogue, toolName) {
    return catalogue.formHints?.get(toolName);
}
export function contextActionFor(catalogue, toolName) {
    return catalogue.contextActions?.get(toolName);
}
export function requestMarkerBinderFor(catalogue, toolName) {
    return catalogue.requestMarkerBinders?.get(toolName);
}
export function reconciliationFor(catalogue, toolName) {
    return catalogue.reconciliations?.get(toolName);
}
/**
 * Structural validation of a declared catalogue, at boot rather than at call
 * time — the same discipline `assertHandlersMatchManifest` applies to the
 * static half.
 */
export function assertRemoteCatalogue(definition) {
    const catalogue = definition.remoteTools;
    if (catalogue === undefined)
        return;
    const problems = [];
    if (catalogue.transport.kind !== "mcp") {
        problems.push("a remote catalogue must declare a remote transport");
    }
    if (!/^https:\/\/[^\s]+$/.test(catalogue.transport.serverUrl)) {
        problems.push("the catalogue endpoint must be an absolute https URL");
    }
    // A catalogue-driven connector must declare an EMPTY static tool allowlist.
    //
    // Two allowlists over one transport is two answers to "may this run", and
    // whichever the engine consulted last would win. Neither merge rule is safe: a
    // union lets a static entry bypass the administrator's review, and an
    // intersection silently disables governed tools when a stale entry is left
    // behind. So the type keeps the field and this refuses to let it be used —
    // with a catalogue, the stored governed set is the only allowlist.
    if (catalogue.transport.allowedTools.length > 0) {
        problems.push("a governed catalogue must declare no static allowedTools — the stored catalogue is the allowlist");
    }
    if ((catalogue.transport.writeTools ?? []).length > 0) {
        problems.push("a governed catalogue must declare no static writeTools — the confirmed risk class decides");
    }
    // EVERY connector-owned exact-key map, listed in one place.
    //
    // The list used to name three of them, so `writeGuards` and `riskSuggestions`
    // could ship a padded or blank key and be silently inert — a map that reads
    // as "declared" and behaves as "absent" is the worst of both, and for a
    // SAFETY map it means a guard nobody notices is not running. Adding a map
    // above without adding it here is the mistake this comment exists to stop.
    for (const [label, keys] of [
        ["inputPolicies", catalogue.inputPolicies],
        ["preflights", catalogue.preflights],
        ["resultAdapters", catalogue.resultAdapters],
        ["writeGuards", catalogue.writeGuards],
        ["riskSuggestions", catalogue.riskSuggestions],
        ["serviceKinds", catalogue.serviceKinds],
        ["formHints", catalogue.formHints],
        ["contextActions", catalogue.contextActions],
        ["requestMarkerBinders", catalogue.requestMarkerBinders],
        ["reconciliations", catalogue.reconciliations],
    ]) {
        for (const key of keys?.keys() ?? []) {
            if (key.trim() === "" || key !== key.trim()) {
                problems.push(`${label} declares a key that is not an exact tool name`);
            }
        }
    }
    // A malformed classification token would travel to a presentation layer
    // whose only safe response is to discard it silently. Checked at boot, for
    // SHAPE only: which tokens are renderable is decided where the copy is, not
    // here — see `RemoteServiceKind`.
    for (const [tool, kind] of catalogue.serviceKinds ?? []) {
        if (!isServiceKindToken(kind)) {
            problems.push(`"${tool}" declares a malformed service kind`);
        }
    }
    for (const [tool, policy] of catalogue.contextActions ?? []) {
        if (!isServiceKindToken(policy.serviceKind)) {
            problems.push(`"${tool}" declares a malformed contextual service kind`);
        }
        if (policy.intent.trim() === "" ||
            policy.title.trim() === "" ||
            policy.objectType.trim() === "") {
            problems.push(`"${tool}" declares incomplete contextual action policy`);
        }
    }
    for (const [tool, policy] of catalogue.inputPolicies ?? []) {
        try {
            assertInputPolicy(policy);
        }
        catch {
            problems.push(`"${tool}" declares an invalid input policy`);
        }
    }
    for (const [tool, preflight] of catalogue.preflights ?? []) {
        if (typeof preflight?.check !== "function") {
            problems.push(`"${tool}" declares a preflight without a callable check`);
        }
    }
    for (const [tool, adapter] of catalogue.resultAdapters ?? []) {
        if (typeof adapter?.objectType !== "string" ||
            adapter.objectType.trim() === "" ||
            typeof adapter?.toExternalRef !== "function") {
            problems.push(`"${tool}" declares an incomplete result adapter`);
        }
    }
    for (const [tool, guard] of catalogue.writeGuards ?? []) {
        if (typeof guard?.before !== "function" ||
            typeof guard?.after !== "function") {
            problems.push(`"${tool}" declares an incomplete write guard`);
        }
    }
    for (const [tool, binder] of catalogue.requestMarkerBinders ?? []) {
        if (typeof binder?.bind !== "function") {
            problems.push(`"${tool}" declares a marker binder without callable bind`);
        }
    }
    for (const [tool, reconciliation] of catalogue.reconciliations ?? []) {
        if (typeof reconciliation?.searchToolName !== "string" ||
            reconciliation.searchToolName.trim() === "" ||
            reconciliation.searchToolName !== reconciliation.searchToolName.trim() ||
            typeof reconciliation?.fetchToolName !== "string" ||
            reconciliation.fetchToolName.trim() === "" ||
            reconciliation.fetchToolName !== reconciliation.fetchToolName.trim()) {
            problems.push(`"${tool}" declares inexact reconciliation tool names`);
        }
        if (typeof reconciliation?.searchArguments !== "function" ||
            typeof reconciliation?.interpretSearch !== "function" ||
            typeof reconciliation?.fetchArguments !== "function" ||
            typeof reconciliation?.verifyFetched !== "function") {
            problems.push(`"${tool}" declares an incomplete reconciliation policy`);
        }
    }
    // A DECLARED binding must actually be callable. `tenantBinding` is the one
    // catalogue member that is a bare object rather than a map, so the key sweep
    // above cannot reach it — and a binding declared as `{}` would type-check at
    // no call site and then throw on the first governed call, which is the worst
    // possible moment to discover it.
    if (catalogue.tenantBinding !== undefined &&
        typeof catalogue.tenantBinding.check !== "function") {
        problems.push("tenantBinding must declare a callable check");
    }
    const delegation = catalogue.tenantBinding?.delegation;
    if (delegation !== undefined &&
        (typeof delegation.projectSchema !== "function" ||
            typeof delegation.bindArguments !== "function")) {
        problems.push("tenantBinding delegation must declare callable projectSchema and bindArguments");
    }
    if (problems.length > 0) {
        throw new Error(`invalid remote tool catalogue: ${problems.join("; ")}`);
    }
}
export function inputPolicyFor(catalogue, toolName) {
    return catalogue.inputPolicies?.get(toolName);
}
//# sourceMappingURL=remote-tools.js.map